import { FC, memo } from 'react';
import {
    PlayCircle,
    CheckCircle,
    FileText,
    HelpCircle,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    ChevronDown,
    List,
    ClipboardCheck
} from 'lucide-react';
import {
    Card,
    ProgressBar
} from '@/shared/ui';
import { Module } from '@features/courses/api/module-services';
import { Lesson } from '@features/courses/api/lesson-services';
import { CourseProgress } from '@features/courses/api/progressServices';
import { QuizAttempt } from '@features/courses/api/quiz-services';
import { LESSON_TYPES } from '@/shared/constants';
import ComplianceSidebar from '../ComplianceSidebar/ComplianceSidebar';
import styles from './CoursePlayerSidebar.module.css';

interface CoursePlayerSidebarProps {
    sidebarOpen: boolean;
    onToggleSidebar: () => void;
    sidebarTab: 'content' | 'compliance';
    onTabChange: (tab: 'content' | 'compliance') => void;
    overallProgress: number;
    modules: Module[];
    currentModule: Module | null;
    onModuleSelect: (module: Module) => void;
    lessons: Lesson[];
    currentLesson: Lesson | null;
    onLessonSelect: (lesson: Lesson) => void;
    isLessonCompleted: (lessonId: string) => boolean;
    getModuleProgress: (moduleId: string) => number;
    progressData?: CourseProgress | null;
    totalTime: number; // Daily time
    sessionTime: number; // Current session instruction time
    totalCourseTime: number;
    quizAttempts?: QuizAttempt[];
    timeUntilBreak: number;
}

interface CourseModulesNavigationProps {
    modules: Module[];
    currentModule: Module | null;
    onModuleSelect: (module: Module) => void;
    lessons: Lesson[];
    currentLesson: Lesson | null;
    onLessonSelect: (lesson: Lesson) => void;
    isLessonCompleted: (lessonId: string) => boolean;
    getModuleProgress: (moduleId: string) => number;
    overallProgress: number;
    progressData?: CourseProgress | null;
    quizAttempts?: QuizAttempt[];
}

const CourseModulesNavigation: FC<CourseModulesNavigationProps> = memo(({
    modules,
    currentModule,
    onModuleSelect,
    lessons,
    currentLesson,
    onLessonSelect,
    isLessonCompleted,
    getModuleProgress,
    overallProgress,
    progressData,
    quizAttempts
}) => {
    const getQuizIdForModule = (moduleId: string): string | null => {
        if (moduleId === 'module-introduction') return null;
        if (moduleId === 'module-review-1-5') return 'review-mastery-1-5';
        if (moduleId === 'module-review-6-10') return 'review-mastery-6-10';
        if (moduleId === 'module-final-test') return 'final-exam-mastery';
        if (moduleId.startsWith('module-')) {
            const moduleNum = moduleId.replace('module-', '');
            return `mastery-quiz-${moduleNum}`;
        }
        return null;
    };

    const getModuleGrade = (moduleId: string): number | null => {
        const quizId = getQuizIdForModule(moduleId);
        if (!quizId) return null;
        
        const attempts = quizAttempts?.filter(a => a.quizId === quizId || a.id === quizId) || [];
        if (attempts.length === 0) return null;
        
        const completedAttempts = attempts.filter(a => a.status === 'completed' && typeof a.score === 'number');
        if (completedAttempts.length === 0) return null;
        
        return Math.max(...completedAttempts.map(a => a.score as number));
    };

    const isModuleLocked = (moduleId: string): boolean => {
        // If the entire course is completed (review mode), nothing is locked
        if (progressData?.overallProgress === 100) return false;

        const currentModuleIndex = modules.findIndex(m => m.id === moduleId);
        if (currentModuleIndex <= 0) return false; // First module is always unlocked

        // Topological safety: If the module itself or any subsequent module is already marked completed, it's unlocked
        const hasCompletedSubsequent = modules.slice(currentModuleIndex).some(
            m => progressData?.moduleProgress?.[m.id]?.completed
        );
        if (hasCompletedSubsequent) return false;

        const prevModule = modules[currentModuleIndex - 1];
        return !progressData?.moduleProgress?.[prevModule.id]?.completed;
    };

    return (
        <>
            <div className={styles.sidebarHeader}>
                <h3>Course Content</h3>
                <ProgressBar progress={overallProgress} size="small" />
            </div>
            <div className={styles.modulesList}>
                {modules.map((module) => {
                    const locked = isModuleLocked(module.id);
                    return (
                        <Card 
                            key={module.id} 
                            className={`
                                ${styles.moduleCard} 
                                ${locked ? styles.moduleCardLocked : ''}
                            `}
                        >
                            <div 
                                className={`
                                    ${styles.moduleHeader} 
                                    ${locked ? styles.moduleHeaderLocked : ''}
                                `} 
                                onClick={() => !locked && onModuleSelect(module)}
                            >
                                <div className={styles.moduleHeaderMain}>
                                    <h4>{module.title}</h4>
                                    {(() => {
                                        const grade = getModuleGrade(module.id);
                                        if (grade === null) return null;
                                        return (
                                            <span className={styles.gradeBadge} data-testid={`grade-badge-${module.id}`}>
                                                {Math.round(grade)}%
                                            </span>
                                        );
                                    })()}
                                </div>
                                <ProgressBar progress={getModuleProgress(module.id)} size="small" />
                            </div>
                            {currentModule?.id === module.id && !locked && (
                                <div className={styles.lessonsList}>
                                    {lessons.map((lesson) => {
                                        const isActiveLesson = currentLesson?.id === lesson.id;
                                        const isCompleted = isLessonCompleted(lesson.id);
                                        const isLocked = (() => {
                                            if (locked) return true;
                                            if (progressData?.overallProgress === 100) return false;
                                            if (isCompleted || isActiveLesson) return false;
                                            if (lesson.order === 1) return false;
                                            const prevLesson = lessons.find(l => l.order === lesson.order - 1);
                                            return prevLesson ? !isLessonCompleted(prevLesson.id) : false;
                                        })();

                                        return (
                                            <div
                                                key={lesson.id}
                                                className={`
                                                    ${styles.lessonItem}
                                                    ${isActiveLesson ? styles.active : ''}
                                                    ${isCompleted ? styles.completed : ''}
                                                    ${isLocked ? styles.locked : ''}
                                                `}
                                                onClick={() => !isLocked && onLessonSelect(lesson)}
                                            >
                                                <span className={styles.lessonIcon}>
                                                    {isCompleted ? <CheckCircle size={14} /> : (
                                                        lesson.type === LESSON_TYPES.VIDEO ? <PlayCircle size={14} /> :
                                                            lesson.type === LESSON_TYPES.QUIZ ? <HelpCircle size={14} /> :
                                                                <FileText size={14} />
                                                    )}
                                                </span>
                                                <span className={styles.lessonName}>{lesson.title}</span>
                                                {isActiveLesson && <span className={styles.nowPlaying}>Now Playing</span>}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Card>
                    );
                })}
            </div>
        </>
    );
});

/**
 * CoursePlayerSidebar component
 * Manages course content navigation and compliance progress display.
 */
const CoursePlayerSidebar: FC<CoursePlayerSidebarProps> = memo(({
    sidebarOpen,
    onToggleSidebar,
    sidebarTab,
    onTabChange,
    overallProgress,
    modules,
    currentModule,
    onModuleSelect,
    lessons,
    currentLesson,
    onLessonSelect,
    isLessonCompleted,
    getModuleProgress,
    progressData,
    totalTime,
    sessionTime,
    totalCourseTime,
    quizAttempts,
    timeUntilBreak,
}) => {
    const activeModuleProgress = currentModule ? getModuleProgress(currentModule.id) : 0;

    return (
        <>
            {/* Backdrop */}
            {sidebarOpen && (
                <div 
                    className={styles.backdrop} 
                    onClick={onToggleSidebar}
                    aria-hidden="true"
                />
            )}

            <div
                className={`
                    ${styles.sidebar} 
                    ${!sidebarOpen ? styles.sidebarCollapsed : ''}
                `}
                data-testid="sidebar"
            >
                <div className={styles.sidebarContent}>
                    {/* Sidebar Tabs */}
                    <div className={styles.sidebarTabs}>
                        <button
                            className={`${styles.tabButton} ${sidebarTab === 'content' ? styles.tabActive : ''}`}
                            onClick={() => onTabChange('content')}
                            title="Course Content"
                        >
                            <List size={20} />
                            <span>Content</span>
                        </button>
                        <button
                            className={`${styles.tabButton} ${sidebarTab === 'compliance' ? styles.tabActive : ''}`}
                            onClick={() => onTabChange('compliance')}
                            title="Compliance Progress"
                        >
                            <ClipboardCheck size={20} />
                            <span>Progress</span>
                        </button>
                    </div>

                    {sidebarTab === 'content' ? (
                        <CourseModulesNavigation
                            modules={modules}
                            currentModule={currentModule}
                            onModuleSelect={onModuleSelect}
                            lessons={lessons}
                            currentLesson={currentLesson}
                            onLessonSelect={(lesson) => {
                                onLessonSelect(lesson);
                                onToggleSidebar(); // Auto-close on selection
                            }}
                            isLessonCompleted={isLessonCompleted}
                            getModuleProgress={getModuleProgress}
                            overallProgress={overallProgress}
                            progressData={progressData}
                            quizAttempts={quizAttempts}
                        />
                    ) : (
                        <ComplianceSidebar
                            progress={progressData || undefined}
                            totalTimeDailySeconds={totalTime}
                            sessionTimeSeconds={sessionTime}
                            totalCourseTimeSeconds={totalCourseTime}
                            timeUntilBreak={timeUntilBreak}
                            currentModuleProgress={activeModuleProgress}
                        />
                    )}
                </div>
            </div>

            {/* Collapse Handle */}
            <div
                className={`
                    ${styles.collapseHandle} 
                    ${sidebarOpen ? styles.handleOpen : ''}
                `}
                onClick={onToggleSidebar}
                data-testid="sidebar-toggle"
            >
                <div className={styles.collapseIcon}>
                    {sidebarOpen ? <ChevronLeft size={24} /> : <List size={24} />}
                </div>
            </div>
        </>
    );
});

export default CoursePlayerSidebar;
